import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MessageSquare, FileText, BarChart3, Image as ImageIcon, Film, Globe,
  Send, Loader2, Upload, Plus, Trash2, LogOut, Sparkles, ArrowLeft, Download,
  MessageCircle, ShieldCheck, ShieldOff, Copy, X, CalendarClock, Zap,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODELS = [
  { id: 'gemini-flash', label: '⚡ Gemini Flash (fastest)' },
  { id: 'haiku', label: '⚡ Claude Haiku (fast)' },
  { id: 'gpt-mini', label: '⚡ GPT-5 mini (fast)' },
  { id: 'gpt', label: 'GPT-5 (deep)' },
  { id: 'claude', label: 'Claude Sonnet 4.5 (deep)' },
  { id: 'gemini', label: 'Gemini 2.5 Pro (deep)' },
];

const QUICK_PROMPTS = [
  { emoji: '🍓', label: 'Strawberry farms near Stellenbosch', text: 'Where can I go pick strawberries near Stellenbosch this weekend?' },
  { emoji: '📅', label: 'Plan my week', text: "Help me plan a productive week. Ask me 3 quick questions first, then draft it." },
  { emoji: '💡', label: 'Idea generator', text: 'Give me 5 side-project ideas I can build in a weekend using AI.' },
  { emoji: '✍️', label: 'Rewrite this', text: 'Rewrite the following email to sound friendlier and shorter:\n\n' },
  { emoji: '🎨', label: 'Explain like I\'m 5', text: 'Explain quantum computing like I\'m five, using an analogy about LEGO.' },
  { emoji: '💰', label: 'Compare things', text: 'Compare Notion, Obsidian, and Craft for personal knowledge management.' },
];

const TABS = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'docs', label: 'Doc Q&A', icon: FileText },
  { id: 'data', label: 'Data Analyst', icon: BarChart3 },
  { id: 'image', label: 'Image', icon: ImageIcon },
  { id: 'video', label: 'Video', icon: Film },
  { id: 'research', label: 'Research', icon: Globe },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
];

export default function AIAssistant() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('chat');
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');

  useEffect(() => {
    if (!token) navigate('/admin');
  }, [token, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  const logout = () => { localStorage.removeItem('adminToken'); navigate('/admin'); };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white flex flex-col">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-white/10 bg-black/30 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="text-gray-400 hover:text-white flex items-center gap-2" data-testid="back-btn">
            <ArrowLeft size={18} /> Admin
          </button>
          <div className="flex items-center gap-2 flex-1">
            <Sparkles className="text-indigo-400" size={22} />
            <h1 className="text-xl font-bold">Kishen's Assistant</h1>
          </div>
          <Button variant="ghost" onClick={logout} className="text-gray-400 hover:text-white" data-testid="ai-logout">
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
        <nav className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-2.5 flex items-center gap-2 text-sm border-b-2 transition-colors ${
                  tab === t.id ? 'border-indigo-400 text-white' : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl w-full mx-auto p-3 md:p-6 flex-1 flex flex-col min-h-0">
        {tab === 'chat' && <ChatPanel headers={headers} />}
        {tab === 'docs' && <DocsPanel headers={headers} />}
        {tab === 'data' && <DataPanel headers={headers} />}
        {tab === 'image' && <ImagePanel headers={headers} />}
        {tab === 'video' && <VideoPanel headers={headers} />}
        {tab === 'research' && <ResearchPanel headers={headers} />}
        {tab === 'whatsapp' && <WhatsAppPanel headers={headers} />}
      </main>
    </div>
  );
}

// ============ Chat ============
function ChatPanel({ headers }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [model, setModel] = useState('gemini-flash');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try { const r = await axios.get(`${API}/ai/sessions`, { headers }); setSessions(r.data); }
    catch { /* silent */ }
  };
  const loadMessages = async (id) => {
    if (!id) return setMessages([]);
    const r = await axios.get(`${API}/ai/sessions/${id}/messages`, { headers });
    setMessages(r.data);
  };
  const newChat = async () => {
    const r = await axios.post(`${API}/ai/sessions`, { title: 'New Chat' }, { headers });
    setSid(r.data.id); setMessages([]); loadSessions();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this chat?')) return;
    await axios.delete(`${API}/ai/sessions/${id}`, { headers });
    if (sid === id) { setSid(null); setMessages([]); }
    loadSessions();
  };
  const send = async () => {
    if (!text.trim()) return;
    let id = sid;
    if (!id) { const r = await axios.post(`${API}/ai/sessions`, { title: text.slice(0, 40) }, { headers }); id = r.data.id; setSid(id); loadSessions(); }
    setMessages(m => [...m, { role: 'user', text, model }]);
    const q = text; setText(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/ai/chat`, { session_id: id, text: q, model }, { headers });
      setMessages(m => [...m, { role: 'assistant', text: r.data.text, model }]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Chat failed'); }
    finally { setBusy(false); }
  };

  useEffect(() => { loadSessions(); }, []); // eslint-disable-line
  useEffect(() => { loadMessages(sid); }, [sid]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const runPrompt = (t) => { setText(t); };

  return (
    <div className="grid md:grid-cols-[240px_1fr] gap-4 h-[calc(100dvh-var(--ai-chrome,180px))] md:h-[calc(100dvh-200px)]">
      <aside className="bg-black/40 rounded-xl border border-white/10 p-3 overflow-y-auto hidden md:block" data-testid="chat-sessions">
        <Button onClick={newChat} className="w-full mb-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-500/20" data-testid="new-chat-btn"><Plus size={14} className="mr-2" /> New Chat</Button>
        {sessions.map(s => (
          <div key={s.id} className={`p-2 rounded flex items-center gap-2 cursor-pointer hover:bg-white/5 transition-colors ${sid === s.id ? 'bg-white/10' : ''}`} onClick={() => setSid(s.id)}>
            <div className="flex-1 truncate text-sm">{s.title}</div>
            <button onClick={(e) => { e.stopPropagation(); del(s.id); }} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
      </aside>
      <section className="bg-gradient-to-br from-slate-900/60 via-black/40 to-purple-950/30 rounded-xl border border-white/10 flex flex-col overflow-hidden shadow-2xl shadow-indigo-500/5">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages" style={{ scrollBehavior: 'smooth' }}>
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/40 animate-pulse-slow">
                <Sparkles className="text-white" size={30} />
              </div>
              <h3 className="text-lg font-bold mb-1">Hey Kishen 👋</h3>
              <p className="text-sm text-gray-400 mb-6">Pick a quick idea or ask me anything.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {QUICK_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => runPrompt(p.text)}
                    data-testid={`quick-prompt-${i}`}
                    className="text-left p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-indigo-500/40 transition-all group"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg group-hover:scale-110 transition-transform">{p.emoji}</span>
                      <span className="text-xs text-gray-300 leading-tight">{p.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => {
            const isUser = m.role === 'user';
            return (
              <div key={i} className={`flex gap-2 items-start message-appear ${isUser ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${isUser ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-gradient-to-br from-emerald-400 to-cyan-500'}`}>
                  {isUser ? <span className="text-xs font-bold text-white">K</span> : <Sparkles size={14} className="text-white" />}
                </div>
                <div className={`max-w-[85%] p-3 rounded-2xl ${isUser ? 'bg-gradient-to-br from-indigo-500/20 to-purple-500/10 border border-indigo-400/30 rounded-tr-sm' : 'bg-white/5 border border-white/10 rounded-tl-sm'}`}>
                  <div className="text-[10px] text-gray-400 mb-1 uppercase tracking-wider">{isUser ? 'You' : (m.model || 'ai')}</div>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</div>
                </div>
              </div>
            );
          })}
          {busy && (
            <div className="flex gap-2 items-start message-appear">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-400 to-cyan-500 shadow-md">
                <Sparkles size={14} className="text-white animate-pulse" />
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm p-3 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-indigo-400 typing-dot" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 rounded-full bg-indigo-400 typing-dot" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 rounded-full bg-indigo-400 typing-dot" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-white/10 flex gap-2 items-end bg-black/30 backdrop-blur" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <select value={model} onChange={e => setModel(e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs max-w-[130px]" data-testid="model-select">
            {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <Textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Message Kishen's Assistant…" className="bg-black/40 border-white/10 text-white flex-1 min-h-[44px] max-h-32 rounded-xl" data-testid="chat-input" />
          <Button onClick={send} disabled={busy || !text.trim()} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-xl shadow-lg shadow-indigo-500/30" data-testid="send-btn"><Send size={16} /></Button>
        </div>
      </section>
    </div>
  );
}

// ============ Doc Q&A ============
function DocsPanel({ headers }) {
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState('Extract all text, preserve reading order, then summarize the key points.');
  const [model, setModel] = useState('gemini');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [sid] = useState(() => crypto.randomUUID());

  const run = async () => {
    if (!file) return toast.error('Select a file');
    setBusy(true); setAnswer('');
    const fd = new FormData();
    fd.append('session_id', sid); fd.append('model', model); fd.append('prompt', prompt); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/ai/vision`, fd, { headers });
      setAnswer(r.data.text);
    } catch (e) { toast.error(e.response?.data?.detail || 'Vision failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-black/40 rounded-xl border border-white/10 p-4 space-y-3">
        <h2 className="font-bold flex items-center gap-2"><FileText size={18} /> Upload Document</h2>
        <input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files?.[0])} className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-indigo-600 file:text-white hover:file:bg-indigo-700" data-testid="docs-file" />
        {file && <p className="text-xs text-gray-400">{file.name} — {(file.size / 1024).toFixed(1)} KB</p>}
        <select value={model} onChange={e => setModel(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm">
          {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} className="bg-black/40 border-white/10 text-white min-h-[100px]" data-testid="docs-prompt" />
        <Button onClick={run} disabled={busy || !file} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="docs-run">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14} /> Analyzing…</> : <><Upload size={14} className="mr-2" /> Extract & Analyze</>}
        </Button>
      </div>
      <div className="bg-black/40 rounded-xl border border-white/10 p-4 min-h-[300px]">
        <h3 className="font-bold mb-2">Answer</h3>
        <div className="whitespace-pre-wrap text-sm text-gray-200" data-testid="docs-answer">{answer || <span className="text-gray-500">No answer yet.</span>}</div>
      </div>
    </div>
  );
}

// ============ Data Analyst ============
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
const CHART_COLORS = ['#818cf8', '#34d399', '#fbbf24', '#f472b6', '#22d3ee'];

function DataChart({ spec, data, whitelist, headers }) {
  const containerRef = useRef(null);
  const [busySend, setBusySend] = useState(false);
  const [sendTo, setSendTo] = useState('');

  if (!spec || !data || spec.chart_type === 'none' || !data.length) return null;
  const { chart_type, x, y = [], title } = spec;

  const chartToPngBlob = () => new Promise((resolve, reject) => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return reject(new Error('Chart not rendered'));
    // Clone and inject white background for readability on WhatsApp
    const clone = svg.cloneNode(true);
    const rect = clone.viewBox?.baseVal || { width: svg.clientWidth, height: svg.clientHeight };
    const w = rect.width || svg.clientWidth || 800;
    const h = rect.height || svg.clientHeight || 400;
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);
    // Inject a solid background rect at the front
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%'); bg.setAttribute('height', '100%'); bg.setAttribute('fill', '#0f172a');
    clone.insertBefore(bg, clone.firstChild);
    const svgStr = new XMLSerializer().serializeToString(clone);
    const svg64 = btoa(unescape(encodeURIComponent(svgStr)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * 2; canvas.height = h * 2; // 2x for retina
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png', 0.92);
    };
    img.onerror = () => reject(new Error('svg->img failed'));
    img.src = 'data:image/svg+xml;base64,' + svg64;
  });

  const downloadPng = async () => {
    try {
      const blob = await chartToPngBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${(title || 'chart').replace(/[^a-z0-9]+/gi, '_')}.png`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || 'Download failed'); }
  };

  const sendToWhatsApp = async () => {
    if (!sendTo) return toast.error('Pick a whitelisted number');
    setBusySend(true);
    try {
      const blob = await chartToPngBlob();
      const fd = new FormData();
      fd.append('to', sendTo);
      fd.append('caption', title || 'Chart');
      fd.append('file', blob, 'chart.png');
      const r = await axios.post(`${API}/ai/whatsapp/send-media`, fd, { headers });
      toast.success(`Sent — Twilio SID ${r.data.twilio_sid.slice(0, 12)}…`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Send failed'); }
    finally { setBusySend(false); }
  };

  return (
    <div className="bg-black/40 rounded-xl border border-white/10 p-4" ref={containerRef}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-bold text-sm">{title || 'Chart'}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={downloadPng} size="sm" variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="chart-download">
            <Download size={13} className="mr-1" /> PNG
          </Button>
          {(whitelist?.length ?? 0) > 0 && (
            <>
              <select value={sendTo} onChange={e => setSendTo(e.target.value)} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs font-mono" data-testid="chart-send-to">
                <option value="">— WhatsApp to —</option>
                {whitelist.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <Button onClick={sendToWhatsApp} disabled={busySend || !sendTo} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="chart-send-wa">
                {busySend ? <Loader2 className="animate-spin" size={13} /> : <><Send size={13} className="mr-1" /> WhatsApp</>}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="w-full h-72" data-testid="data-chart">
        <ResponsiveContainer>
          {chart_type === 'bar' && (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey={x} stroke="#aaa" fontSize={11} />
              <YAxis stroke="#aaa" fontSize={11} />
              <RTooltip contentStyle={{ background: '#111', border: '1px solid #333' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {y.map((k, i) => <Bar key={k} dataKey={k} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </BarChart>
          )}
          {chart_type === 'line' && (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey={x} stroke="#aaa" fontSize={11} />
              <YAxis stroke="#aaa" fontSize={11} />
              <RTooltip contentStyle={{ background: '#111', border: '1px solid #333' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {y.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />)}
            </LineChart>
          )}
          {chart_type === 'pie' && y[0] && (
            <PieChart>
              <RTooltip contentStyle={{ background: '#111', border: '1px solid #333' }} />
              <Pie data={data} dataKey={y[0]} nameKey={x} cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 10 }}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
            </PieChart>
          )}
          {chart_type === 'scatter' && y[0] && (
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey={x} stroke="#aaa" fontSize={11} />
              <YAxis dataKey={y[0]} stroke="#aaa" fontSize={11} />
              <RTooltip contentStyle={{ background: '#111', border: '1px solid #333' }} />
              <Scatter data={data} fill={CHART_COLORS[0]} />
            </ScatterChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DataPanel({ headers }) {
  const [file, setFile] = useState(null);
  const [question, setQuestion] = useState('Summarize the dataset and highlight anomalies.');
  const [model, setModel] = useState('gpt');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sid] = useState(() => crypto.randomUUID());
  const [followupQ, setFollowupQ] = useState('');
  const [busyF, setBusyF] = useState(false);
  const [waWhitelist, setWaWhitelist] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ai/whatsapp/config`, { headers })
      .then(r => setWaWhitelist(r.data.whitelist || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const run = async () => {
    if (!file) return toast.error('Select a CSV or Excel file');
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append('session_id', sid); fd.append('question', question); fd.append('model', model); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/ai/analyze-data`, fd, { headers });
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Analysis failed'); }
    finally { setBusy(false); }
  };

  const followup = async () => {
    if (!followupQ.trim()) return;
    setBusyF(true);
    try {
      const r = await axios.post(`${API}/ai/data-followup`, { session_id: sid, question: followupQ, model }, { headers });
      setResult(prev => ({ ...prev, ...r.data, preview: prev?.preview || [] }));
      setFollowupQ('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Follow-up failed'); }
    finally { setBusyF(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-black/40 rounded-xl border border-white/10 p-4 space-y-3">
        <h2 className="font-bold flex items-center gap-2"><BarChart3 size={18} /> Upload CSV / Excel</h2>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0])} className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-indigo-600 file:text-white hover:file:bg-indigo-700" data-testid="data-file" />
        {file && <p className="text-xs text-gray-400">{file.name}</p>}
        <select value={model} onChange={e => setModel(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm">
          {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <Textarea value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask about the data in plain English" className="bg-black/40 border-white/10 text-white min-h-[80px]" data-testid="data-question" />
        <Button onClick={run} disabled={busy || !file} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="data-run">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14} /> Analyzing…</> : 'Analyze'}
        </Button>
      </div>
      {result && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-black/40 rounded-xl border border-white/10 p-4">
              <h3 className="font-bold mb-2">Answer</h3>
              <p className="text-xs text-gray-400 mb-2">{result.rows} rows · {result.columns.length} columns</p>
              <div className="whitespace-pre-wrap text-sm text-gray-200" data-testid="data-answer">{result.text}</div>
            </div>
            <div className="bg-black/40 rounded-xl border border-white/10 p-4 overflow-auto">
              <h3 className="font-bold mb-2">Preview (first 5 rows)</h3>
              {result.preview?.length ? (
                <table className="text-xs w-full">
                  <thead><tr>{result.columns.map(c => <th key={c} className="text-left px-2 py-1 border-b border-white/10">{c}</th>)}</tr></thead>
                  <tbody>{result.preview.map((row, i) => (
                    <tr key={i}>{result.columns.map(c => <td key={c} className="px-2 py-1 border-b border-white/5 text-gray-300">{String(row[c] ?? '')}</td>)}</tr>
                  ))}</tbody>
                </table>
              ) : <p className="text-xs text-gray-500 italic">Preview from initial upload.</p>}
            </div>
          </div>
          <DataChart spec={result.chart_spec} data={result.chart_data} whitelist={waWhitelist} headers={headers} />
          {/* Follow-up question */}
          <div className="bg-black/40 rounded-xl border border-white/10 p-4 space-y-2">
            <h3 className="font-bold text-sm flex items-center gap-2"><MessageSquare size={14} /> Follow-up question (same dataset, no re‑upload)</h3>
            <div className="flex gap-2">
              <Textarea
                value={followupQ}
                onChange={e => setFollowupQ(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); followup(); } }}
                placeholder="Which month had the biggest jump? Group by category. Show me a pie chart of..."
                className="bg-black/40 border-white/10 text-white min-h-[50px] flex-1"
                data-testid="data-followup"
              />
              <Button onClick={followup} disabled={busyF || !followupQ.trim()} className="bg-indigo-600 hover:bg-indigo-700 self-stretch" data-testid="data-followup-run">
                {busyF ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              </Button>
            </div>
            <p className="text-[10px] text-gray-500">Dataset cached for 24 h in this session.</p>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Image Gen ============
function ImagePanel({ headers }) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState([]);

  const load = async () => {
    try { const r = await axios.get(`${API}/ai/gallery?kind=ai-image`, { headers }); setGallery(r.data); } catch {}
  };
  const gen = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/ai/generate-image`, { prompt }, { headers }); setPrompt(''); load(); toast.success('Image generated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="bg-black/40 rounded-xl border border-white/10 p-4 space-y-3">
        <h2 className="font-bold flex items-center gap-2"><ImageIcon size={18} /> Image Generation (GPT Image 1)</h2>
        <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe the image you want…" className="bg-black/40 border-white/10 text-white min-h-[80px]" data-testid="image-prompt" />
        <Button onClick={gen} disabled={busy || !prompt.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="image-gen">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14} /> Generating…</> : 'Generate'}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="image-gallery">
        {gallery.map(g => (
          <a key={g.id} href={`${BACKEND_URL}${g.url}`} target="_blank" rel="noopener noreferrer" className="block bg-black/40 border border-white/10 rounded-xl overflow-hidden hover:border-indigo-400">
            <img src={`${BACKEND_URL}${g.url}`} alt={g.prompt} className="w-full h-40 object-cover" loading="lazy" />
            <p className="p-2 text-xs text-gray-400 truncate">{g.prompt}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

// ============ Video Gen ============
function VideoPanel({ headers }) {
  const [prompt, setPrompt] = useState('');
  const [seconds, setSeconds] = useState(4);
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState([]);

  const load = async () => {
    try { const r = await axios.get(`${API}/ai/gallery?kind=ai-video`, { headers }); setGallery(r.data); } catch {}
  };
  const gen = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/ai/generate-video`, { prompt, seconds }, { headers });
      setPrompt(''); load(); toast.success('Video generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="bg-black/40 rounded-xl border border-white/10 p-4 space-y-3">
        <h2 className="font-bold flex items-center gap-2"><Film size={18} /> Sora 2 Video Generation</h2>
        <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe the video…" className="bg-black/40 border-white/10 text-white min-h-[80px]" data-testid="video-prompt" />
        <div className="flex gap-2 items-center">
          <label className="text-sm text-gray-400">Seconds:</label>
          <Input type="number" min="4" max="12" step="4" value={seconds} onChange={e => setSeconds(parseInt(e.target.value) || 4)} className="w-20 bg-black/40 border-white/10 text-white" />
          <Button onClick={gen} disabled={busy || !prompt.trim()} className="bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="video-gen">
            {busy ? <><Loader2 className="animate-spin mr-2" size={14} /> Generating (may take 1-2 min)…</> : 'Generate'}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {gallery.map(g => (
          <div key={g.id} className="bg-black/40 border border-white/10 rounded-xl overflow-hidden">
            <video src={`${BACKEND_URL}${g.url}`} controls className="w-full" />
            <p className="p-2 text-xs text-gray-400 truncate">{g.prompt}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ URL Research ============
function ResearchPanel({ headers }) {
  const [urls, setUrls] = useState('');
  const [question, setQuestion] = useState('');
  const [model, setModel] = useState('gpt');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [sid] = useState(() => crypto.randomUUID());

  const run = async () => {
    const urlList = urls.split('\n').map(s => s.trim()).filter(Boolean);
    if (!urlList.length) return toast.error('Paste at least one URL');
    if (!question.trim()) return toast.error('Enter a research question');
    setBusy(true); setAnswer(null);
    try {
      const r = await axios.post(`${API}/ai/research-url`, { session_id: sid, urls: urlList, question, model }, { headers });
      setAnswer(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Research failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-black/40 rounded-xl border border-white/10 p-4 space-y-3">
        <h2 className="font-bold flex items-center gap-2"><Globe size={18} /> URL Research (with sources)</h2>
        <Textarea value={urls} onChange={e => setUrls(e.target.value)} placeholder={"Paste up to 5 URLs, one per line\nhttps://…\nhttps://…"} className="bg-black/40 border-white/10 text-white min-h-[100px] font-mono text-xs" data-testid="research-urls" />
        <Textarea value={question} onChange={e => setQuestion(e.target.value)} placeholder="What do you want to know from these sources?" className="bg-black/40 border-white/10 text-white min-h-[60px]" data-testid="research-question" />
        <div className="flex gap-2">
          <select value={model} onChange={e => setModel(e.target.value)} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm">
            {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <Button onClick={run} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="research-run">
            {busy ? <><Loader2 className="animate-spin mr-2" size={14} /> Fetching + analyzing…</> : 'Research'}
          </Button>
        </div>
      </div>
      {answer && (
        <div className="bg-black/40 rounded-xl border border-white/10 p-4">
          <h3 className="font-bold mb-2">Answer</h3>
          <div className="whitespace-pre-wrap text-sm text-gray-200 mb-4" data-testid="research-answer">{answer.text}</div>
          <h4 className="text-xs uppercase text-gray-400 mb-1">Sources</h4>
          <ol className="text-xs space-y-1">
            {answer.sources.map(s => (
              <li key={s.n}>[{s.n}] <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline break-all">{s.url}</a> <span className="text-gray-600">({s.chars} chars)</span></li>
            ))}
          </ol>
          {answer.failed?.length > 0 && (
            <p className="text-xs text-red-400 mt-2">Failed: {answer.failed.map(f => f.url).join(', ')}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============ WhatsApp Approved Template Sender ============
function TemplateSender({ headers, whitelist, onSent, defaultSid }) {
  const [to, setTo] = useState('');
  const [csid, setCsid] = useState('');
  const [varsText, setVarsText] = useState('{"1":"Hello"}');
  const [sending, setSending] = useState(false);

  const send = async () => {
    let variables = {};
    try { variables = varsText.trim() ? JSON.parse(varsText) : {}; }
    catch { return toast.error('Variables must be valid JSON, e.g. {"1":"hi"}'); }
    setSending(true);
    try {
      const r = await axios.post(`${API}/ai/whatsapp/send-template`, {
        to, content_sid: csid || undefined, variables,
      }, { headers });
      toast.success(`Template sent — Twilio SID ${r.data.twilio_sid.slice(0, 12)}…`);
      onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Template send failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2 pt-2 border-t border-white/10">
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase text-gray-500">To (whitelisted)</label>
          <select
            value={to}
            onChange={e => setTo(e.target.value)}
            className="w-full mt-1 bg-black/40 border border-white/10 rounded px-2 py-2 text-sm font-mono"
            data-testid="wa-template-to"
          >
            <option value="">— pick a number —</option>
            {(whitelist || []).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500">ContentSid (blank = default)</label>
          <Input
            value={csid}
            onChange={e => setCsid(e.target.value)}
            placeholder={defaultSid || 'HX…'}
            className="bg-black/40 border-white/10 text-white font-mono mt-1"
            data-testid="wa-template-sid"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase text-gray-500">Variables (JSON: {"{\"1\":\"...\",\"2\":\"...\"}"})</label>
        <Textarea
          value={varsText}
          onChange={e => setVarsText(e.target.value)}
          className="bg-black/40 border-white/10 text-white font-mono text-xs mt-1"
          data-testid="wa-template-vars"
        />
      </div>
      <Button onClick={send} disabled={sending || !to || (!csid && !defaultSid)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wa-template-send">
        {sending ? <><Loader2 className="animate-spin mr-2" size={14} /> Sending template…</> : 'Send template'}
      </Button>
    </div>
  );
}

// ============ WhatsApp AI Bridge ============
function WhatsAppPanel({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newNum, setNewNum] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testText, setTestText] = useState('Hello from Kishen\'s Assistant');
  const [testing, setTesting] = useState(false);
  const [messages, setMessages] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/ai/whatsapp/config`, { headers }),
        axios.get(`${API}/ai/whatsapp/messages?limit=50`, { headers }),
      ]);
      setCfg(c.data);
      setMessages(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load WhatsApp config'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/ai/whatsapp/config`, patch, { headers });
      setCfg(prev => ({ ...prev, ...r.data }));
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addNum = () => {
    const n = newNum.trim();
    if (!n) return;
    const list = [...(cfg.whitelist || []), n];
    save({ whitelist: list });
    setNewNum('');
  };
  const removeNum = (n) => {
    save({ whitelist: cfg.whitelist.filter(x => x !== n) });
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/ai/whatsapp/test-send`, { to: testTo, text: testText }, { headers });
      toast.success(`Sent — Twilio SID ${r.data.twilio_sid.slice(0, 12)}…`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setTesting(false); }
  };

  const copyWebhook = () => {
    const url = `${BACKEND_URL}/api/ai/whatsapp/webhook`;
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied');
  };

  if (loading) return <div className="text-gray-400 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  if (!cfg) return null;

  const webhookUrl = `${BACKEND_URL}/api/ai/whatsapp/webhook`;

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Config column */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-black/40 rounded-xl border border-white/10 p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-bold flex items-center gap-2"><MessageCircle size={20} /> WhatsApp AI Bridge</h2>
              <p className="text-sm text-gray-400 mt-1">Message the Twilio number and Kishen's Assistant replies on your phone. Whitelisted numbers only.</p>
            </div>
            <button
              onClick={() => save({ enabled: !cfg.enabled })}
              disabled={saving}
              data-testid="wa-toggle-enabled"
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${cfg.enabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-red-500/10 text-red-300 border border-red-500/30'}`}
            >
              {cfg.enabled ? <><ShieldCheck size={14} /> Enabled</> : <><ShieldOff size={14} /> Disabled</>}
            </button>
          </div>

          {!cfg.twilio_configured && (
            <div className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded p-3">
              Twilio env vars are missing. Set <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, and <code>TWILIO_WHATSAPP_FROM</code> in backend/.env.
            </div>
          )}

          <div>
            <label className="text-xs uppercase text-gray-400">Default AI model</label>
            <select
              value={cfg.default_model}
              onChange={e => save({ default_model: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm"
              data-testid="wa-model-select"
            >
              {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase text-gray-400">System prompt (persona)</label>
            <Textarea
              defaultValue={cfg.system_prompt}
              onBlur={e => { if (e.target.value !== cfg.system_prompt) save({ system_prompt: e.target.value }); }}
              className="bg-black/40 border-white/10 text-white min-h-[80px] mt-1"
              data-testid="wa-system-prompt"
            />
          </div>

          {/* Voice mode */}
          <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-white/10">
            <div>
              <label className="text-xs uppercase text-gray-400">Voice notes</label>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => save({ voice_enabled: !cfg.voice_enabled })}
                  className={`flex-1 px-3 py-2 rounded text-sm ${cfg.voice_enabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/5 text-gray-400 border border-white/10'}`}
                  data-testid="wa-voice-toggle"
                >
                  {cfg.voice_enabled ? 'Enabled' : 'Off'}
                </button>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Whisper transcribes incoming voice notes.</p>
            </div>
            <div>
              <label className="text-xs uppercase text-gray-400">Reply as</label>
              <select
                value={cfg.voice_reply}
                onChange={e => save({ voice_reply: e.target.value })}
                className="w-full mt-1 bg-black/40 border border-white/10 rounded px-2 py-2 text-sm"
                data-testid="wa-voice-reply"
              >
                <option value="text">Text only</option>
                <option value="voice">Voice only</option>
                <option value="both">Text + Voice</option>
              </select>
              <p className="text-[10px] text-gray-500 mt-1">When incoming was a voice note.</p>
            </div>
            <div>
              <label className="text-xs uppercase text-gray-400">TTS voice</label>
              <select
                value={cfg.tts_voice}
                onChange={e => save({ tts_voice: e.target.value })}
                className="w-full mt-1 bg-black/40 border border-white/10 rounded px-2 py-2 text-sm"
                data-testid="wa-tts-voice"
              >
                {['alloy','nova','echo','onyx','shimmer','fable','coral','sage','ash'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Rate limit */}
          <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-white/10">
            <div>
              <label className="text-xs uppercase text-gray-400">Cooldown (seconds per message)</label>
              <Input
                type="number"
                min="0" max="60"
                defaultValue={cfg.min_seconds_between}
                onBlur={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v !== cfg.min_seconds_between) save({ min_seconds_between: v }); }}
                className="bg-black/40 border-white/10 text-white mt-1"
                data-testid="wa-cooldown"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-gray-400">Daily cap per number</label>
              <Input
                type="number"
                min="1" max="10000"
                defaultValue={cfg.max_per_day}
                onBlur={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v !== cfg.max_per_day) save({ max_per_day: v }); }}
                className="bg-black/40 border-white/10 text-white mt-1"
                data-testid="wa-daily-cap"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase text-gray-400">Whitelisted numbers ({cfg.whitelist.length})</label>
              <span className="text-xs text-gray-500">Only these numbers get AI replies</span>
            </div>
            <div className="space-y-1.5 mb-2">
              {cfg.whitelist.length === 0 && <p className="text-sm text-gray-500 italic">No numbers whitelisted — every incoming message will be silently ignored.</p>}
              {cfg.whitelist.map(n => (
                <div key={n} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-3 py-2" data-testid={`wa-number-${n}`}>
                  <span className="font-mono text-sm flex-1">{n}</span>
                  <button onClick={() => removeNum(n)} className="text-red-400 hover:text-red-300" data-testid={`wa-remove-${n}`}><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newNum}
                onChange={e => setNewNum(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addNum(); }}
                placeholder="+27720350706"
                className="bg-black/40 border-white/10 text-white font-mono"
                data-testid="wa-new-number"
              />
              <Button onClick={addNum} disabled={saving || !newNum.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wa-add-number">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Use E.164 format (e.g. +27720350706). Spaces and dashes are stripped.</p>
          </div>
        </div>

        {/* Approved template send */}
        <div className="bg-black/40 rounded-xl border border-white/10 p-5 space-y-3">
          <h3 className="font-bold text-sm flex items-center gap-2"><MessageCircle size={14} /> Approved template send (proactive, any time)</h3>
          <p className="text-xs text-gray-400">Bypasses the 24‑hour session rule using a Twilio‑approved WhatsApp template. Create the template in Twilio Console → Content Editor, then paste its <code>HX…</code> ContentSid below.</p>
          <div>
            <label className="text-xs uppercase text-gray-400">Default ContentSid</label>
            <Input
              defaultValue={cfg.content_sid}
              onBlur={e => { if (e.target.value !== cfg.content_sid) save({ content_sid: e.target.value }); }}
              placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="bg-black/40 border-white/10 text-white font-mono mt-1"
              data-testid="wa-content-sid"
            />
          </div>
          <TemplateSender headers={headers} whitelist={cfg.whitelist} onSent={load} defaultSid={cfg.content_sid} />
        </div>

        {/* Daily Briefing */}
        <div className="bg-black/40 rounded-xl border border-white/10 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-sm flex items-center gap-2"><CalendarClock size={14} /> Daily Briefing</h3>
            <button
              onClick={() => save({ briefing: { ...(cfg.briefing || {}), enabled: !cfg.briefing?.enabled } })}
              className={`px-3 py-1 rounded text-xs ${cfg.briefing?.enabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/5 text-gray-400 border border-white/10'}`}
              data-testid="wa-briefing-toggle"
            >
              {cfg.briefing?.enabled ? 'Enabled' : 'Off'}
            </button>
          </div>
          <p className="text-xs text-gray-400">Every day at the chosen UTC time, Kishen's Assistant will DM you a fresh AI briefing.</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-gray-500">Hour (UTC)</label>
              <Input type="number" min="0" max="23" defaultValue={cfg.briefing?.hour_utc ?? 7}
                onBlur={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) save({ briefing: { ...(cfg.briefing || {}), hour_utc: v } }); }}
                className="bg-black/40 border-white/10 text-white mt-1" data-testid="wa-briefing-hour" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-500">Minute (UTC)</label>
              <Input type="number" min="0" max="59" defaultValue={cfg.briefing?.minute_utc ?? 30}
                onBlur={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) save({ briefing: { ...(cfg.briefing || {}), minute_utc: v } }); }}
                className="bg-black/40 border-white/10 text-white mt-1" data-testid="wa-briefing-min" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-gray-500">Target number (whitelisted)</label>
            <select
              value={cfg.briefing?.target || ''}
              onChange={e => save({ briefing: { ...(cfg.briefing || {}), target: e.target.value } })}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded px-2 py-2 text-sm font-mono"
              data-testid="wa-briefing-target"
            >
              <option value="">— pick a number —</option>
              {(cfg.whitelist || []).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-gray-500">Briefing prompt</label>
            <Textarea
              defaultValue={cfg.briefing?.prompt || ''}
              onBlur={e => { if (e.target.value !== cfg.briefing?.prompt) save({ briefing: { ...(cfg.briefing || {}), prompt: e.target.value } }); }}
              className="bg-black/40 border-white/10 text-white min-h-[70px] mt-1 text-xs"
              data-testid="wa-briefing-prompt"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!cfg.briefing?.use_template}
              onChange={e => save({ briefing: { ...(cfg.briefing || {}), use_template: e.target.checked } })}
              data-testid="wa-briefing-tpl"
            />
            <label className="text-xs text-gray-300">Send via approved template (bypasses 24 h rule)</label>
          </div>
          {cfg.briefing?.use_template && (
            <div>
              <label className="text-[10px] uppercase text-gray-500">Template variable key for AI body</label>
              <Input
                defaultValue={cfg.briefing?.template_variable_key || '1'}
                onBlur={e => { if (e.target.value !== cfg.briefing?.template_variable_key) save({ briefing: { ...(cfg.briefing || {}), template_variable_key: e.target.value } }); }}
                className="bg-black/40 border-white/10 text-white font-mono mt-1"
                data-testid="wa-briefing-varkey"
              />
              <p className="text-[10px] text-gray-500 mt-1">Uses default ContentSid above. The AI briefing text is passed as this variable.</p>
            </div>
          )}
          {cfg.briefing?.last_sent_date && (
            <p className="text-[10px] text-gray-500">Last sent: {cfg.briefing.last_sent_date}</p>
          )}
          <Button
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/ai/whatsapp/briefing/run-now`, {}, { headers });
                toast.success('Briefing sent — check WhatsApp'); load();
              } catch (e) { toast.error(e.response?.data?.detail?.reason || e.response?.data?.detail || 'Failed'); }
            }}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="wa-briefing-run"
          >
            <Zap size={13} className="mr-1" /> Send briefing now
          </Button>
        </div>

        {/* Test send */}
        <div className="bg-black/40 rounded-xl border border-white/10 p-5 space-y-3">
          <h3 className="font-bold text-sm flex items-center gap-2"><Send size={14} /> Test send (no AI, just deliverability)</h3>
          <p className="text-xs text-amber-200/80 bg-amber-500/10 border border-amber-500/30 rounded p-2">
            WhatsApp 24‑hour session rule: cold outbound text needs a Twilio‑approved template. This button works only if the recipient has messaged the Twilio number in the last 24 h (or joined the sandbox). The AI reply webhook is not affected.
          </p>
          <div className="flex gap-2">
            <Input
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              placeholder="+27720350706"
              className="bg-black/40 border-white/10 text-white font-mono flex-1"
              data-testid="wa-test-to"
            />
          </div>
          <Textarea
            value={testText}
            onChange={e => setTestText(e.target.value)}
            className="bg-black/40 border-white/10 text-white"
            data-testid="wa-test-text"
          />
          <Button onClick={runTest} disabled={testing || !testTo || !testText} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wa-test-send">
            {testing ? <><Loader2 className="animate-spin mr-2" size={14} /> Sending…</> : 'Send test WhatsApp'}
          </Button>
        </div>
      </div>

      {/* Setup + log column */}
      <div className="space-y-4">
        <div className="bg-black/40 rounded-xl border border-white/10 p-5 space-y-3">
          <h3 className="font-bold text-sm">Twilio setup</h3>
          <ol className="text-xs text-gray-300 space-y-2 list-decimal ml-4">
            <li>Open the <a className="text-indigo-400 hover:underline" href="https://console.twilio.com/us1/develop/sms/senders" target="_blank" rel="noopener noreferrer">Twilio Console</a>, go to <b>Messaging → Senders → WhatsApp Senders</b> (or use the WhatsApp Sandbox for testing).</li>
            <li>Set the <b>“When a message comes in”</b> webhook to:
              <div className="mt-1 flex items-center gap-1 bg-black/60 p-2 rounded border border-white/10">
                <code className="text-[10px] break-all flex-1">{webhookUrl}</code>
                <button onClick={copyWebhook} className="text-indigo-400 hover:text-indigo-300" data-testid="wa-copy-webhook"><Copy size={12} /></button>
              </div>
              Method: <code>HTTP POST</code>.
            </li>
            <li>For sandbox testing, send <code>join &lt;your-code&gt;</code> from your phone to <code className="break-all">{cfg.twilio_from}</code>.</li>
            <li>Message the number — Kishen's Assistant will reply using the model above and keep per‑phone memory.</li>
          </ol>
        </div>

        <div className="bg-black/40 rounded-xl border border-white/10 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm">Recent messages</h3>
            <button onClick={load} className="text-xs text-indigo-400 hover:underline">Refresh</button>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="wa-messages">
            {messages.length === 0 && <p className="text-xs text-gray-500 italic">No traffic yet.</p>}
            {messages.map(m => (
              <div key={m.id} className={`text-xs p-2 rounded border ${m.direction === 'in' ? 'bg-blue-500/5 border-blue-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                <div className="flex items-center justify-between gap-2 text-gray-400 mb-1">
                  <span className="font-mono">{m.direction === 'in' ? `← ${m.from}` : `→ ${m.to}`}</span>
                  <span className={`px-1.5 rounded ${m.status === 'ok' ? 'bg-emerald-500/20 text-emerald-300' : m.status === 'blocked' ? 'bg-amber-500/20 text-amber-200' : 'bg-red-500/20 text-red-300'}`}>{m.status}</span>
                </div>
                <div className="whitespace-pre-wrap text-gray-200 line-clamp-4">{m.body}</div>
                <div className="text-[10px] text-gray-500 mt-1">{new Date(m.created_at).toLocaleString()}{m.model ? ` · ${m.model}` : ''}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
